import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import emptyFunction from 'common/util/emptyFunction';

import { SR_API_ACTION_UTILITY_PARTNER_GET_BY_AUTOCOMPLETE } from 'api/utilityActionTypes';
import formatUtilityPartnerId from 'common/utility/formatUtilityPartnerId';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrUtilityProfitOptionCard from 'client/components/SrUtilityProfitOptionCard/SrUtilityProfitOptionCard';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

const getFormFields = ({ onFindUtilityPartners, autoFocus } = {}) => [
  {
    id: 'searchInput',
    title: 'Search partner name or slug',
    type: 'autocomplete',
    properties: {
      placeholder: 'type partner name or slug here...',
      choices: [],
      onSuggestionsFetchRequested: ({ value }) => {
        return onFindUtilityPartners({ searchInput: value });
      },
      filterFunc: () => {
        return true;
      },
      autoFocus: autoFocus,
    },
  },
];

export const UPPartnerSearchFormInner = ({ autoFocus, onPartnerSelected }) => {
  const [utilityPartnerMatches, setUtilityPartnerMatches] = useState([]);
  const dispatch = useDispatch();

  const onFindUtilityPartners = ({ searchInput }) => {
    if (!searchInput) {
      setUtilityPartnerMatches([]);
      return;
    }

    dispatch(requestUtilityApi, {
      queryId: searchInput,
      action: SR_API_ACTION_UTILITY_PARTNER_GET_BY_AUTOCOMPLETE,
      payload: {
        searchInput,
        limit: 10,
      },
    }).then((autocompletePredictions) => {
      setUtilityPartnerMatches(autocompletePredictions || []);
    });
  };

  const formFields = getFormFields({
    onFindUtilityPartners,
    utilityPartnerMatches,
    autoFocus,
  });

  return (
    <Formik
      initialValues={createInitialValues({ formFields })}
      enableReinitialize
      validate={createFormValidationFn(formFields)}
      onSubmit={emptyFunction}
      render={(formikBag) => {
        const searchInput = formikBag.values?.searchInput;

        return (
          <Form>
            {formFields.map((formField) => (
              <SrFormikField
                key={formField.id}
                formField={formField}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            ))}
            {searchInput && utilityPartnerMatches.length === 0 && (
              <SrBox textAlign="center" paddingTop="20px">
                <SrText weight="800" font="greycliff" size="16">
                  That partner does not currently exist in our system.
                </SrText>
              </SrBox>
            )}
            {searchInput &&
              utilityPartnerMatches.map((utilityPartner) => {
                return (
                  <SrBox paddingTop="23px" key={utilityPartner.id}>
                    <SrUtilityProfitOptionCard
                      title={utilityPartner.name}
                      // Admin-only search picker: show the canonical backend partner ID
                      // (`utilityPartners.id`) next to the slug so admins can tell apart
                      // partners with similar names before opening the edit view (FEAT-E72).
                      subtitle={
                        <>
                          {utilityPartner.slug}
                          {' · '}
                          {formatUtilityPartnerId(utilityPartner.id, { withLabel: true })}
                        </>
                      }
                      detailColor="white"
                      imageUrl={utilityPartner.logoUrl}
                      onAddClick={() => {
                        setUtilityPartnerMatches([]);
                        formikBag.setFieldValue('searchInput', '');
                        onPartnerSelected(utilityPartner);
                      }}
                    />
                  </SrBox>
                );
              })}
          </Form>
        );
      }}
    />
  );
};

UPPartnerSearchFormInner.propTypes = {
  onPartnerSelected: PropTypes.func.isRequired,
};

const UPPartnerSearchForm = ({ subtitle, errorMessage, onClose, onPartnerSelected }) => {
  return (
    <SrDarkModal onClose={onClose}>
      <SrModalContent width={500} innerStyle={{ padding: 0 }}>
        <SrBox textAlign="center" borderBottom="2px solid #e9ebec" padding="20px 0">
          <SrText
            font="greycliff"
            weight="800"
            lineHeight="25px"
            fontSize="25px"
            display="block"
            paddingBottom="8px"
          >
            Search partners
          </SrText>
          <SrBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="80%"
            margin="0 auto"
          >
            <SrText font="greycliff" weight="600" lineHeight="19px" fontSize="16px">
              {subtitle}
            </SrText>
          </SrBox>
        </SrBox>
        <SrBox padding="0 40px 40px">
          <UPPartnerSearchFormInner autoFocus onPartnerSelected={onPartnerSelected} />
          {errorMessage && <SrErrorMessage>{errorMessage}</SrErrorMessage>}
        </SrBox>
      </SrModalContent>
    </SrDarkModal>
  );
};

UPPartnerSearchForm.propTypes = {
  subtitle: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onPartnerSelected: PropTypes.func.isRequired,
};

UPPartnerSearchForm.displayName = 'UPPartnerSearchForm';

export default UPPartnerSearchForm;
